import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  Save,
  CreditCard,
  Package,
  Percent,
  Crown,
  Settings,
  Camera,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useUserProfile, useUpdateUserProfile, useUploadAvatar } from '../../hooks/useUserProfile';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';

export default function AccountSettings() {
  const { data: profile, isLoading: profileLoading } = useUserProfile();
  const updateProfile = useUpdateUserProfile();
  const { uploadAvatar, uploading: avatarUploading } = useUploadAvatar();
  const { company, role } = useCurrentCompany();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Sync form state when profile loads
  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
      setPhone(profile.phone || '');
    }
  }, [profile]);

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      await updateProfile.mutateAsync({
        first_name: firstName,
        last_name: lastName,
        phone: phone,
      });
      setSaveStatus('saved');
      setTimeout(() => { if (mountedRef.current) setSaveStatus('idle'); }, 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => { if (mountedRef.current) setSaveStatus('idle'); }, 3000);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await uploadAvatar(file);
    } catch (err) {
      console.error('Avatar upload failed:', err);
    }
  };

  const hasChanges =
    profile &&
    (firstName !== (profile.first_name || '') ||
      lastName !== (profile.last_name || '') ||
      phone !== (profile.phone || ''));

  if (profileLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-[#363232] rounded w-48" />
          <div className="h-64 bg-[#363232] rounded" />
          <div className="h-48 bg-[#363232] rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#9B1421]/20 flex items-center justify-center">
            <Settings size={20} className="text-text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#EBFFEE]">Account Settings</h1>
            <p className="text-sm text-[#A9A3A3]">Manage your profile and business settings</p>
          </div>
        </div>
      </div>

      {/* Your Profile Section */}
      <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
        <CardHeader>
          <CardTitle className="text-[#EBFFEE] flex items-center gap-2">
            <User className="w-5 h-5 text-rollops-accent" />
            Your Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="!p-6">
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Avatar */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-[#363232] flex items-center justify-center overflow-hidden border-2 border-[#504A4A]">
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-10 h-10 text-[#706A6A]" />
                  )}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={avatarUploading}
                  aria-label="Upload avatar"
                  className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center text-white hover:bg-[#E8535E] transition-colors"
                >
                  <Camera className="w-4 h-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                />
              </div>
              {avatarUploading && (
                <span className="text-xs text-[#A9A3A3]">Uploading...</span>
              )}
            </div>

            {/* Form Fields */}
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#A9A3A3] mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-[#363232] border border-[#504A4A] text-[#EBFFEE] placeholder-[#706A6A] focus:outline-none focus:border-[#9B1421] transition-colors"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#A9A3A3] mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-[#363232] border border-[#504A4A] text-[#EBFFEE] placeholder-[#706A6A] focus:outline-none focus:border-[#9B1421] transition-colors"
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#A9A3A3] mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={profile?.email || ''}
                  disabled
                  className="w-full px-3 py-2 rounded-lg bg-[#2a2727] border border-[#504A4A] text-[#706A6A] cursor-not-allowed"
                />
                <p className="text-xs text-[#706A6A] mt-1">
                  Email is managed through your authentication provider
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#A9A3A3] mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#363232] border border-[#504A4A] text-[#EBFFEE] placeholder-[#706A6A] focus:outline-none focus:border-[#9B1421] transition-colors"
                  placeholder="Phone number"
                />
              </div>

              {/* Save Button */}
              <div className="flex items-center gap-3 pt-2">
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges || saveStatus === 'saving'}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    hasChanges
                      ? 'bg-[#9B1421] hover:bg-[#E8535E] text-white'
                      : 'bg-[#363232] text-[#706A6A] cursor-not-allowed'
                  }`}
                >
                  <Save className="w-4 h-4" />
                  {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
                </Button>
                {saveStatus === 'saved' && (
                  <span className="flex items-center gap-1 text-sm text-green-400">
                    <CheckCircle2 className="w-4 h-4" />
                    Saved
                  </span>
                )}
                {saveStatus === 'error' && (
                  <span className="flex items-center gap-1 text-sm text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    Failed to save
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Settings Section */}
      {company && ['owner', 'admin'].includes(role || '') && (
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE] flex items-center gap-2">
              <Settings className="w-5 h-5 text-rollops-accent" />
              Business Settings
              <span className="text-xs text-[#A9A3A3] font-normal ml-2">
                {company.name}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="!p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link
                to="/billing/settings"
                className="flex items-center gap-4 p-4 rounded-lg bg-[#363232] border border-[#504A4A] hover:border-[#9B1421] hover:bg-[#363232]/80 transition-all group"
              >
                <div className="w-10 h-10 rounded-lg bg-[#9B1421]/20 flex items-center justify-center group-hover:bg-[#9B1421]/30 transition-colors">
                  <CreditCard className="w-5 h-5 text-[#E8535E]" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#EBFFEE]">Billing & Stripe Setup</h3>
                  <p className="text-xs text-[#A9A3A3]">Payment processing, late fees, connect account</p>
                </div>
              </Link>

              <Link
                to="/subscriptions-plans/gym"
                className="flex items-center gap-4 p-4 rounded-lg bg-[#363232] border border-[#504A4A] hover:border-[#9B1421] hover:bg-[#363232]/80 transition-all group"
              >
                <div className="w-10 h-10 rounded-lg bg-[#9B1421]/20 flex items-center justify-center group-hover:bg-[#9B1421]/30 transition-colors">
                  <Package className="w-5 h-5 text-[#E8535E]" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#EBFFEE]">Subscription Plans</h3>
                  <p className="text-xs text-[#A9A3A3]">Create and manage membership tiers</p>
                </div>
              </Link>

              <Link
                to="/billing/discount-codes"
                className="flex items-center gap-4 p-4 rounded-lg bg-[#363232] border border-[#504A4A] hover:border-[#9B1421] hover:bg-[#363232]/80 transition-all group"
              >
                <div className="w-10 h-10 rounded-lg bg-[#9B1421]/20 flex items-center justify-center group-hover:bg-[#9B1421]/30 transition-colors">
                  <Percent className="w-5 h-5 text-[#E8535E]" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#EBFFEE]">Discount Codes</h3>
                  <p className="text-xs text-[#A9A3A3]">Promotional discounts and trial offers</p>
                </div>
              </Link>

              <Link
                to="/subscriptions-plans/rollops"
                className="flex items-center gap-4 p-4 rounded-lg bg-[#363232] border border-[#504A4A] hover:border-[#9B1421] hover:bg-[#363232]/80 transition-all group"
              >
                <div className="w-10 h-10 rounded-lg bg-[#9B1421]/20 flex items-center justify-center group-hover:bg-[#9B1421]/30 transition-colors">
                  <Crown className="w-5 h-5 text-[#E8535E]" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#EBFFEE]">RollOps Plan</h3>
                  <p className="text-xs text-[#A9A3A3]">Your RollOps subscription and features</p>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
